/**
 * Authentication Context
 *
 * Provides Firebase authentication state and methods throughout the app.
 *
 * Pattern: docs/design-patterns/auth/social-provider-management.md
 */

import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type AuthError,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  signOut as firebaseSignOut,
  getAdditionalUserInfo,
  GithubAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  type OAuthCredential,
  onIdTokenChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  signInWithEmailAndPassword,
  signInWithPopup,
  unlink,
  updatePassword,
  updateProfile,
  type User,
} from 'firebase/auth';

import { getApiConfig } from '../config';
import {
  auth,
  getSendChangeEmailVerification,
  getSendPasswordResetEmail,
  getSendVerificationEmail,
} from '../config/firebase';
import { clearActiveOrgId } from '../utils/activeOrg';
import {
  AccountLinkFailedError,
  AccountLinkRequiredError,
  EmailNotVerifiedError,
  ProviderLinkConflictError,
  ReauthRequiredError,
} from '../utils/auth-errors';
import { logger } from '../utils/logger';

import { AuthContext } from './AuthContext.context';

// ---------------------------------------------------------------------------
// Pending credential — in-memory only (intentionally NOT sessionStorage)
//
// Storing OAuth tokens in sessionStorage serialises them as plain JSON visible
// in DevTools and writable by any XSS payload. An attacker could plant their
// own valid credential there, which would silently get linked to the next user
// who completes a sign-in on the same tab.
//
// Keeping the credential in module-level memory means:
//   - It is never serialised or written to any browser storage
//   - It is cleared automatically on page refresh
//   - It is not reachable from other tabs or extensions
//   - An XSS attacker would still need to be executing during the same JS
//     context to interfere — the attack surface is materially smaller
//
// Trade-off: if the user refreshes mid-flow the pending credential is lost
// and they restart the OAuth flow — acceptable UX cost for the security gain.
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  children: ReactNode;
}

// Two separate in-memory slots:
//
// _pendingOAuthCredential — for GitHub↔Google provider linking.
//   Uses linkWithCredential(), which VERIFIES an existing credential then merges accounts.
//
// _pendingPassword — for adding email/password to an existing OAuth account.
//   linkWithCredential() with EmailAuthProvider calls signInWithPassword internally,
//   which fails with INVALID_PASSWORD when no password exists yet. The correct API
//   is updatePassword(), which calls accounts:update to SET a new password directly.
//
// _pendingEmail — the email address that initiated the linking flow.
//   Both slots are only consumed if the signed-in user's email matches _pendingEmail,
//   preventing a different user signing in on the same tab from inheriting a pending link.

let _pendingOAuthCredential: OAuthCredential | null = null;
let _pendingPassword: string | null = null;
let _pendingEmail: string | null = null;

function buildGithubProvider(): GithubAuthProvider {
  const provider = new GithubAuthProvider();
  provider.addScope('user:email');
  provider.addScope('read:user');
  return provider;
}

function buildGoogleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.addScope('email');
  provider.addScope('profile');
  return provider;
}

async function linkPendingCredentialToUser(user: User): Promise<void> {
  // Guard: only link if the signed-in user is the same person who initiated the flow.
  if (_pendingEmail && user.email !== _pendingEmail) {
    logger.warn(
      {
        component: 'AuthContext',
        pendingEmail: _pendingEmail,
        signedInEmail: user.email ?? 'none',
      },
      'Pending link email mismatch — discarding pending credential',
    );
    _pendingOAuthCredential = null;
    _pendingPassword = null;
    _pendingEmail = null;
    return;
  }

  // OAuth → OAuth linking (e.g. GitHub credential to an existing Google account)
  if (_pendingOAuthCredential) {
    const pending = _pendingOAuthCredential;
    _pendingOAuthCredential = null;
    _pendingEmail = null;
    try {
      await linkWithCredential(user, pending);
      logger.info({ component: 'AuthContext' }, `Linked pending ${pending.providerId} credential`);
    } catch (err) {
      const code = (err as AuthError).code;
      if (code === 'auth/provider-already-linked' || code === 'auth/credential-already-in-use') {
        return;
      }
      logger.error(
        { component: 'AuthContext', errorCode: code ?? 'unknown' },
        'Failed to link pending OAuth credential',
      );
      throw new AccountLinkFailedError(pending.providerId);
    }
  }

  // Email/password → OAuth linking: set a new password on the existing account.
  // linkWithCredential(EmailAuthProvider.credential()) calls signInWithPassword
  // internally, which fails with INVALID_PASSWORD when no password exists yet.
  // updatePassword() calls accounts:update instead, setting the password directly.
  if (_pendingPassword) {
    const password = _pendingPassword;
    _pendingPassword = null;
    _pendingEmail = null;
    try {
      await updatePassword(user, password);
      logger.info({ component: 'AuthContext' }, 'Added email/password sign-in via updatePassword');
    } catch (err) {
      const code = (err as AuthError).code;
      logger.error(
        { component: 'AuthContext', errorCode: code ?? 'unknown' },
        'Failed to set password via updatePassword',
      );
      throw new AccountLinkFailedError('password');
    }
  }
}

function storePendingCredential(credential: OAuthCredential, email: string): void {
  _pendingOAuthCredential = credential;
  _pendingEmail = email;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const previousUserRef = useRef<User | null>(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    // onIdTokenChanged is a superset of onAuthStateChanged — it fires on sign-in,
    // sign-out, AND on token refresh (e.g. after user.getIdToken(true)). Using it
    // here ensures that after linkProviderWithGithub or unlinkProvider calls
    // getIdToken(true) to pull in updated custom claims, React re-renders with the
    // new user object (including reloadUserInfo.customAttributes for githubUsername).
    const unsubscribe = onIdTokenChanged(auth, newUser => {
      const previousUser = previousUserRef.current;
      const isInitialMount = isInitialMountRef.current;
      isInitialMountRef.current = false;

      // Track user change
      const userChanged = previousUser?.uid !== newUser?.uid;
      previousUserRef.current = newUser;

      // If user logged in (was null, now has user) and it's not the initial mount, invalidate queries
      // This ensures we refresh data when switching users or logging in after logout
      if (!isInitialMount && !previousUser && newUser) {
        logger.info({ component: 'AuthContext' }, 'User logged in, invalidating queries');

        // Clear stale activeOrgId from localStorage (user may have different orgs)
        clearActiveOrgId();
        logger.debug({ component: 'AuthContext' }, 'Cleared stale activeOrgId on login');

        void queryClient.invalidateQueries({ queryKey: ['projects'] });
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
        void queryClient.invalidateQueries({ queryKey: ['invitations'] });
      } else if (!isInitialMount && userChanged && newUser) {
        // User switched (different user ID)
        logger.info({ component: 'AuthContext' }, 'User changed, invalidating queries');

        // Clear stale activeOrgId from localStorage (different user, different orgs)
        clearActiveOrgId();
        logger.debug({ component: 'AuthContext' }, 'Cleared stale activeOrgId on user switch');

        void queryClient.invalidateQueries({ queryKey: ['projects'] });
        void queryClient.invalidateQueries({ queryKey: ['tasks'] });
        void queryClient.invalidateQueries({ queryKey: ['invitations'] });
      }

      setUser(newUser);
      setLoading(false);
    });

    return unsubscribe;
  }, [queryClient]);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (!result.user.emailVerified) {
      // Send a fresh verification email via our custom Cloud Function, sign out
      // immediately, then surface the error. The user has no active session and
      // the backend will reject any API call with email_verified: false.
      try {
        await getSendVerificationEmail()({});
      } catch {
        // Best-effort — if sending fails (e.g. throttled) we still sign out
      }
      await firebaseSignOut(auth);
      throw new EmailNotVerifiedError(email);
    }
    await linkPendingCredentialToUser(result.user);
  }, []);

  const signUp = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // Send verification email via our custom Cloud Function and immediately
      // sign out. The user cannot access the app until they click the link —
      // the backend gate rejects tokens with email_verified: false.
      try {
        await getSendVerificationEmail()({});
      } catch (sendErr) {
        await firebaseSignOut(auth);
        const code = (sendErr as { code?: string }).code ?? '';
        if (code === 'functions/resource-exhausted') {
          throw new Error('verification-email-rate-limited');
        }
        throw new Error('verification-email-failed');
      }
      await firebaseSignOut(auth);
      throw new EmailNotVerifiedError(email);
    } catch (err) {
      if (err instanceof EmailNotVerifiedError) throw err;
      const authErr = err as AuthError;
      if (authErr.code === 'auth/email-already-in-use') {
        // Email exists under a different provider (e.g. Google or GitHub).
        // Store the raw password so updatePassword() can set it after the user
        // authenticates with their existing provider. We cannot use
        // linkWithCredential(EmailAuthProvider.credential(...)) here because that
        // calls signInWithPassword internally — which fails when no password exists yet.
        _pendingPassword = password;
        _pendingEmail = email;
        throw new AccountLinkRequiredError(email, 'password');
      }
      throw err;
    }
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<void> => {
    try {
      const result = await signInWithPopup(auth, buildGoogleProvider());
      await linkPendingCredentialToUser(result.user);
    } catch (err) {
      const authErr = err as AuthError;
      if (authErr.code === 'auth/account-exists-with-different-credential') {
        const email = authErr.customData?.email as string;
        const pending = GoogleAuthProvider.credentialFromError(authErr);
        if (pending) storePendingCredential(pending, email);
        throw new AccountLinkRequiredError(email, 'google.com');
      }
      throw err;
    }
  }, []);

  const signInWithGithub = useCallback(async (): Promise<void> => {
    try {
      const result = await signInWithPopup(auth, buildGithubProvider());
      const additionalInfo = getAdditionalUserInfo(result);
      const githubUsername = additionalInfo?.username ?? null;
      if (githubUsername) {
        logger.info({ component: 'AuthContext', githubUsername }, 'GitHub username captured');
      }
      await linkPendingCredentialToUser(result.user);
    } catch (err) {
      const authErr = err as AuthError;
      if (authErr.code === 'auth/account-exists-with-different-credential') {
        const email = authErr.customData?.email as string;
        const pending = GithubAuthProvider.credentialFromError(authErr);
        if (pending) storePendingCredential(pending, email);
        throw new AccountLinkRequiredError(email, 'github.com');
      }
      throw err;
    }
  }, []);

  const signOut = useCallback((): Promise<void> => {
    return firebaseSignOut(auth);
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<void> => {
    try {
      await getSendPasswordResetEmail()({ email });
    } catch (sendErr) {
      const code = (sendErr as { code?: string }).code ?? '';
      if (code === 'functions/resource-exhausted') {
        throw new Error('password-reset-rate-limited');
      }
      throw new Error('password-reset-failed');
    }
  }, []);

  const sendChangeEmailVerification = useCallback(async (newEmail: string): Promise<void> => {
    try {
      await getSendChangeEmailVerification()({ newEmail });
    } catch (sendErr) {
      const code = (sendErr as { code?: string }).code ?? '';
      if (code === 'functions/resource-exhausted') {
        throw new Error('change-email-rate-limited');
      }
      throw new Error('change-email-failed');
    }
  }, []);

  const updateUserPassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      if (!user?.email) {
        throw new Error('No user logged in');
      }

      // Reauthenticate user before password change
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);
    },
    [user],
  );

  const updateUserProfile = useCallback(
    async (displayName?: string, photoURL?: string): Promise<void> => {
      if (!user) {
        throw new Error('No user logged in');
      }

      await updateProfile(user, {
        displayName: displayName || '',
        photoURL: photoURL || '',
      });
    },
    [user],
  );

  // ---------------------------------------------------------------------------
  // Provider linking (from Profile page while already signed in)
  // ---------------------------------------------------------------------------

  const linkProviderWithGoogle = useCallback(async (): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    try {
      await linkWithPopup(user, buildGoogleProvider());
      // Reload + force token refresh so the JWT immediately reflects
      // email_verified: true from Google — same pattern as addPassword.
      await user.reload();
      await user.getIdToken(true);
      logger.info({ component: 'AuthContext' }, 'Linked Google provider');
    } catch (err) {
      const code = (err as AuthError).code;
      if (code === 'auth/provider-already-linked') return;
      if (code === 'auth/credential-already-in-use')
        throw new ProviderLinkConflictError('google.com');
      if (code === 'auth/requires-recent-login') throw new ReauthRequiredError();
      throw err;
    }
  }, [user]);

  const linkProviderWithGithub = useCallback(async (): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    try {
      await linkWithPopup(user, buildGithubProvider());
      // Fetch and set the githubUsername custom claim via the backend
      const apiConfig = getApiConfig();
      const token = await user.getIdToken();
      try {
        await fetch(`${apiConfig.baseURL}/auth/github-claim`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        logger.warn(
          { component: 'AuthContext' },
          'Could not set GitHub username claim — will retry on next sign-in',
        );
      }
      await user.getIdToken(true);
      logger.info({ component: 'AuthContext' }, 'Linked GitHub provider');
    } catch (err) {
      const code = (err as AuthError).code;
      if (code === 'auth/provider-already-linked') return;
      if (code === 'auth/credential-already-in-use')
        throw new ProviderLinkConflictError('github.com');
      if (code === 'auth/requires-recent-login') throw new ReauthRequiredError();
      throw err;
    }
  }, [user]);

  const addPassword = useCallback(
    async (password: string): Promise<void> => {
      if (!user?.email) throw new Error('No authenticated user with an email');
      try {
        // linkWithCredential properly links the email/password provider to the
        // existing OAuth account and fires onIdTokenChanged so the UI reflects
        // the new provider immediately. updatePassword() only changes an
        // existing password credential and does NOT add the provider to
        // providerData or trigger an auth state event.
        const credential = EmailAuthProvider.credential(user.email, password);
        await linkWithCredential(user, credential);
        // Firebase can temporarily set email_verified: false in the JWT it
        // issues right after linkWithCredential, even when the email was
        // already verified via Google. Reloading the profile fetches the
        // server-side value (which correctly reflects the Google verification),
        // then forcing a token refresh mints a JWT with email_verified: true.
        await user.reload();
        await user.getIdToken(true);
        logger.info({ component: 'AuthContext' }, 'Added email/password sign-in method');
      } catch (err) {
        const code = (err as AuthError).code;
        if (code === 'auth/requires-recent-login') throw new ReauthRequiredError();
        // auth/provider-already-linked: password was already linked (e.g. a
        // concurrent call succeeded first). Treat as success — the user now
        // has the password provider regardless.
        // auth/email-already-in-use: the Firebase Auth emulator incorrectly
        // returns this when linking email/password to a Google account whose
        // email matches. Check whether the provider was actually linked before
        // surfacing the error to the user.
        if (code === 'auth/provider-already-linked' || code === 'auth/email-already-in-use') {
          // First check if the provider is already linked (handles double-call).
          await user.reload();
          if (user.providerData.some(p => p.providerId === 'password')) {
            await user.getIdToken(true);
            return;
          }
          // Firebase Auth emulator bug: accounts:signUp with idToken (the SDK's
          // mechanism for linking email/password to an OAuth account) incorrectly
          // returns EMAIL_EXISTS. Fall back to updatePassword which uses
          // accounts:update — the emulator handles this correctly. This only
          // affects local dev; production linkWithCredential works as expected.
          if (import.meta.env['VITE_FIREBASE_USE_EMULATOR'] === 'true') {
            // updatePassword uses accounts:update (not accounts:signUp) so it
            // succeeds in the emulator. After setting the password, re-authenticate
            // with the new credential so Firebase registers the password provider
            // in providerData and the UI reflects the linked method correctly.
            await updatePassword(user, password);
            if (user.email) {
              const credential = EmailAuthProvider.credential(user.email, password);
              await reauthenticateWithCredential(user, credential);
            }
            await user.reload();
            await user.getIdToken(true);
            return;
          }
        }
        throw err;
      }
    },
    [user],
  );

  const unlinkProvider = useCallback(
    async (providerId: string): Promise<void> => {
      if (!user) throw new Error('No user logged in');
      try {
        await unlink(user, providerId);
        logger.info({ component: 'AuthContext', providerId }, 'Unlinked provider');

        if (providerId === 'github.com') {
          // Clear the GitHub username custom claim from the backend.
          const apiConfig = getApiConfig();
          const token = await user.getIdToken();
          try {
            await fetch(`${apiConfig.baseURL}/auth/github-claim`, {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch {
            logger.warn(
              { component: 'AuthContext' },
              'Could not clear GitHub username claim — stale claim will expire within 1 hour',
            );
          }
        }

        // Always reload the user profile and force a token refresh after any
        // unlink. This ensures:
        //   1. The UI reflects the removed provider immediately.
        //   2. The JWT's email_verified claim is fresh — unlinking the sole
        //      OAuth provider that granted email_verified:true can drop it to
        //      false in some Firebase versions. We surface that immediately
        //      rather than letting the next API call fail silently.
        await user.reload();
        await user.getIdToken(true);
      } catch (err) {
        const code = (err as AuthError).code;
        if (code === 'auth/no-such-provider') return;
        if (code === 'auth/requires-recent-login') throw new ReauthRequiredError();
        throw err;
      }
    },
    [user],
  );

  // ---------------------------------------------------------------------------
  // Email verification
  // ---------------------------------------------------------------------------

  const resendEmailVerification = useCallback(
    async (email: string, password: string): Promise<void> => {
      // Sign in briefly to get a User object (required by sendEmailVerification),
      // send the email, then immediately sign out. The backend rejects all API
      // calls with email_verified: false, so this brief session is harmless.
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user.emailVerified) {
        // Already verified since the last attempt — just sign out cleanly.
        await firebaseSignOut(auth);
        return;
      }
      try {
        await getSendVerificationEmail()({});
      } catch (sendErr) {
        const code = (sendErr as { code?: string }).code ?? '';
        if (code === 'functions/resource-exhausted') {
          throw new Error('Too many verification emails sent. Please wait before trying again.');
        }
        throw new Error(
          "Couldn't send the verification email. Please try again or contact support.",
        );
      } finally {
        await firebaseSignOut(auth);
      }
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Re-authentication (required before unlinkProvider and other sensitive ops)
  // ---------------------------------------------------------------------------

  const reauthenticateWithPassword = useCallback(
    async (password: string): Promise<void> => {
      if (!user?.email) throw new Error('No email on current user');
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    },
    [user],
  );

  const reauthenticateWithGoogle = useCallback(async (): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    await reauthenticateWithPopup(user, buildGoogleProvider());
  }, [user]);

  const reauthenticateWithGithub = useCallback(async (): Promise<void> => {
    if (!user) throw new Error('No user logged in');
    await reauthenticateWithPopup(user, buildGithubProvider());
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signInWithGithub,
      signOut,
      resetPassword,
      sendChangeEmailVerification,
      updateUserPassword,
      updateUserProfile,
      linkProviderWithGoogle,
      linkProviderWithGithub,
      addPassword,
      unlinkProvider,
      resendEmailVerification,
      reauthenticateWithPassword,
      reauthenticateWithGoogle,
      reauthenticateWithGithub,
    }),
    [
      user,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signInWithGithub,
      signOut,
      resetPassword,
      sendChangeEmailVerification,
      updateUserPassword,
      updateUserProfile,
      linkProviderWithGoogle,
      linkProviderWithGithub,
      addPassword,
      unlinkProvider,
      resendEmailVerification,
      reauthenticateWithPassword,
      reauthenticateWithGoogle,
      reauthenticateWithGithub,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
